import { ChangeEvent } from 'react';
import { Formik, Form, FormikProps, Field } from 'formik';
import { NumericFormat } from 'react-number-format';
import { EditableJob } from '@/services/jobs';
import { trioUITheme } from '@/styles/theme';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import { EditableJobProps, Question, ValuesWithLabel } from '@/types';
import { buildErrorMessage } from '@/utils/formsValidation';
import * as Styles from './JobBasicInfo.styles';
import { dropdownOptionsMap, fieldsAndFriendlyLabels, textInputs } from './JobBasicInfo.utils';
import SectionBox from '../SectionBox';

export type JobBasicInfoComponentProps = Pick<
  EditableJobProps,
  'workingTimes' | 'onSubmit' | 'handleChangeWorkingTimes' | 'onAddNewWorkingTime'
> & {
  touchedFields: string[];
  errorMessages: Record<string, string>;
  job: EditableJob;
  techStacksOptions: ValuesWithLabel<string>[];
  partnerOptions: ValuesWithLabel<string>[];
  onChangeHandler: (
    form: FormikProps<EditableJob>,
    field: Question,
    event: ChangeEvent<HTMLInputElement>,
    submitForm?: boolean
  ) => void;
};

const JobBasicInfoComponent = ({
  job,
  workingTimes,
  onSubmit,
  onChangeHandler,
  touchedFields,
  techStacksOptions,
  partnerOptions,
  errorMessages,
  handleChangeWorkingTimes,
  onAddNewWorkingTime,
}: JobBasicInfoComponentProps): JSX.Element => {
  const handleChange = (event: any, form: FormikProps<any>, field: any): void => {
    onChangeHandler(form, field, event, !textInputs.includes(field.name));
  };

  return (
    <SectionBox data-testid="basic-info-section-title" title="Basic Info" id="basic-info">
      <Formik
        initialValues={{
          ...job,
          salaryRangeMin: job.salaryRange?.min,
          salaryRangeMax: job.salaryRange?.max,
        }}
        onSubmit={onSubmit}
      >
        {(form) => {
          return (
            <Form onSubmit={form.handleSubmit}>
              <Styles.FormGroup key="general-info">
                <Field name="title" key="title">
                  {({ field }) => {
                    return (
                      <Styles.InputWrapper key={field.name}>
                        <InputText
                          onChangeHandler={(event) => handleChange(event, form, field)}
                          onBlurHandler={form.submitForm}
                          label={fieldsAndFriendlyLabels.title}
                          name={field.name}
                          value={field.value}
                          errorMessage={buildErrorMessage(
                            touchedFields.includes(field.name),
                            errorMessages[field.name]
                          )}
                        />
                      </Styles.InputWrapper>
                    );
                  }}
                </Field>

                <Field name="whenToStart" key="whenToStart">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        isMultiple={field.isMultiple}
                        options={dropdownOptionsMap[field.name]}
                        name={field.name}
                        defaultValue={field.value}
                        label={fieldsAndFriendlyLabels.whenToStart}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Field name="account" key="account">
                  {({ field }) => {
                    const value = typeof field.value === 'string' ? field.value : field.value.id;

                    return (
                      <Styles.InputWrapper key={field.name}>
                        <DropDown
                          optionsWithLabel={partnerOptions}
                          name={field.name}
                          defaultValue={value}
                          label={fieldsAndFriendlyLabels.account}
                          errorMessage={buildErrorMessage(
                            touchedFields.includes(field.name),
                            errorMessages[field.name]
                          )}
                          onChangeDropDown={(event) => handleChange(event, form, field)}
                        />
                      </Styles.InputWrapper>
                    );
                  }}
                </Field>

                <Styles.DoubleInputRow>
                  <label htmlFor="salaryRangeMin">{fieldsAndFriendlyLabels.salaryRange}</label>
                  <div>
                    <Field name="salaryRangeMin" key="salaryRangeMin">
                      {({ field }) => (
                        <NumericFormat
                          id="salaryRangeMin"
                          customInput={InputText}
                          thousandSeparator=","
                          prefix="$ "
                          onChangeHandler={() => {}}
                          onValueChange={(_, sourceInfo) =>
                            handleChange(sourceInfo.event, form, field)
                          }
                          onBlur={form.submitForm}
                          name={field.name}
                          value={field.value}
                          errorMessage={buildErrorMessage(
                            touchedFields.includes(field.name),
                            errorMessages[field.name]
                          )}
                        />
                      )}
                    </Field>
                    <span>to</span>
                    <Field name="salaryRangeMax" key="salaryRangeMax">
                      {({ field }) => (
                        <NumericFormat
                          customInput={InputText}
                          thousandSeparator=","
                          prefix="$ "
                          onChangeHandler={() => {}}
                          onValueChange={(_, sourceInfo) =>
                            handleChange(sourceInfo.event, form, field)
                          }
                          onBlur={form.submitForm}
                          label=""
                          name={field.name}
                          value={field.value}
                          errorMessage={buildErrorMessage(
                            touchedFields.includes(field.name),
                            errorMessages[field.name]
                          )}
                        />
                      )}
                    </Field>
                  </div>
                </Styles.DoubleInputRow>

                <Field name="status" key="status">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        isMultiple={field.isMultiple}
                        options={dropdownOptionsMap[field.name]}
                        name={field.name}
                        defaultValue={field.value}
                        label={fieldsAndFriendlyLabels.status}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Field name="jobLink" key="jobLink">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <InputText
                        onChangeHandler={(event) => handleChange(event, form, field)}
                        onBlurHandler={form.submitForm}
                        label={fieldsAndFriendlyLabels.jobLink}
                        name={field.name}
                        color={trioUITheme.colors['primary-60']}
                        value={field.value ?? ''}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>
              </Styles.FormGroup>

              <Styles.FormGroup key="tech-info">
                <Field name="developerType" key="developerType">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        options={dropdownOptionsMap.developerType}
                        name={field.name}
                        defaultValue={field.value}
                        label={fieldsAndFriendlyLabels.developerType}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Field name="numberOfDevs" key="numberOfDevs">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <InputText
                        type="number"
                        onChangeHandler={(event) => handleChange(event, form, field)}
                        onBlurHandler={form.submitForm}
                        label={fieldsAndFriendlyLabels.numberOfDevs}
                        name={field.name}
                        value={field.value}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Field name="techs" key="techs">
                  {({ field }) => {
                    const isFullStack = form.values?.developerType?.includes('Full Stack');
                    const tagSelectorLimit = isFullStack ? 6 : 3;

                    return (
                      <Styles.InputWrapper key={field.name}>
                        <Styles.CustomTagSelector
                          optionsWithLabel={techStacksOptions}
                          selectorLimit={tagSelectorLimit}
                          name={field.name}
                          defaultValue={field.value}
                          label={fieldsAndFriendlyLabels.techs}
                          errorMessage={buildErrorMessage(
                            touchedFields.includes(field.name),
                            errorMessages[field.name]
                          )}
                          onChangeDropDown={(event) => handleChange(event, form, field)}
                        />
                      </Styles.InputWrapper>
                    );
                  }}
                </Field>

                <Field name="experience" key="experience">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        options={dropdownOptionsMap.experience}
                        name={field.name}
                        defaultValue={field.value ?? ''}
                        label={fieldsAndFriendlyLabels.experience}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>
              </Styles.FormGroup>

              <Styles.FormGroup key="time-info">
                <Field name="timeZone" key="timeZone">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        optionsWithLabel={field?.optionsWithLabel}
                        isMultiple={field.isMultiple}
                        options={dropdownOptionsMap[field.name]}
                        name={field.name}
                        defaultValue={field.value ?? ''}
                        label={fieldsAndFriendlyLabels.timeZone}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Field name="timeZoneOverlap" key="timeZoneOverlap">
                  {({ field }) => (
                    <Styles.InputWrapper key={field.name}>
                      <DropDown
                        optionsWithLabel={dropdownOptionsMap[field.name]}
                        isMultiple={field.isMultiple}
                        name={field.name}
                        defaultValue={field.value?.toString() ?? ''}
                        label={fieldsAndFriendlyLabels.timeZoneOverlap}
                        errorMessage={buildErrorMessage(
                          touchedFields.includes(field.name),
                          errorMessages[field.name]
                        )}
                        onChangeDropDown={(event) => handleChange(event, form, field)}
                      />
                    </Styles.InputWrapper>
                  )}
                </Field>

                <Styles.WorkingTimesBox>
                  {workingTimes.map((workingTime, index) => (
                    <Styles.WorkingTimesInputRow>
                      <label htmlFor="workingTimesStart">
                        {index === 0 ? fieldsAndFriendlyLabels.workingTimes : ''}
                      </label>
                      <div>
                        <Styles.CustomDropDown
                          options={dropdownOptionsMap.workingTimes}
                          name="workingTimesStart"
                          key="workingTimesStart"
                          defaultValue={workingTime.start ?? ''}
                          onChangeDropDown={(event) =>
                            handleChangeWorkingTimes(event, index, 'start')
                          }
                        />
                        <span>to</span>
                        <Styles.CustomDropDown
                          options={dropdownOptionsMap.workingTimes}
                          name="workingTimesEnd"
                          key="workingTimesEnd"
                          label=""
                          isDisabled={!workingTime.start}
                          defaultValue={workingTime.end ?? ''}
                          onChangeDropDown={(event) =>
                            handleChangeWorkingTimes(event, index, 'end')
                          }
                        />
                      </div>
                    </Styles.WorkingTimesInputRow>
                  ))}

                  <Styles.CustomTransparentButton isBlueButton onClick={onAddNewWorkingTime}>
                    + Add new working time
                  </Styles.CustomTransparentButton>
                </Styles.WorkingTimesBox>
              </Styles.FormGroup>
            </Form>
          );
        }}
      </Formik>
    </SectionBox>
  );
};

export default JobBasicInfoComponent;
